import { buildUserNav } from '@/domain/layout/app-nav';
import type { INavGroup, INavLink } from '@/domain/layout/app-nav';

/**
 * A page the assistant can take the user to (plan §9.7): the drawer's links
 * of this member, plus the pages the drawer does not list.
 */
export interface IAssistantNavTarget {
  /** The page key of the cards («menu», «stats-orders», «menu-import»…). */
  key: string;
  /** i18n key of the page title. */
  titleKey: string;
  /**
   * i18n key of the drawer group the page sits in («Estadísticas» for its
   * «Pedidos»): page titles are only clear inside their group.
   */
  groupTitleKey?: string;
  path: string;
  /** Query the page needs («?tab=plan»). */
  query?: Record<string, string>;
  /** Lowest plan tier that includes the page (Plus = 1, Pro = 2); null for every plan. */
  minTier: number | null;
  /** The store's plan does not include the page: the assistant shows the plan instead. */
  locked: boolean;
}

export interface IAssistantNavTargetsInput {
  /** The store's drawer navigation, already filtered for the member's role. */
  groups: INavGroup[];
  /** The store's effective plan tier. */
  tier: number;
  /** `/{stringId}`: the base of the pages outside the drawer. */
  basePath: string;
  /** CatalogManage: the menu uploader (`/menu/import`) is a CatalogManage page. */
  canManageCatalog: boolean;
}

/** The menu uploader («Actualizar desde fotos»). */
export const MENU_IMPORT_TARGET = 'menu-import';
/** The plan settings («Ver planes»): the store settings on its plan tab. */
export const PLANS_TARGET = 'plans';
/**
 * The member's own account («Mi cuenta»: name, email and PIN), the user
 * drawer's entry of that key: every member has it, whatever their role and
 * the store's plan.
 */
export const ACCOUNT_TARGET = 'user-account';
/** The drawer entry that holds the plan tab (StoreSettings). */
const STORE_SETTINGS_TARGET = 'store-settings';

/** A page of a card's navigate target (host contract §3.3). */
const PAGE_KEY_PATTERN = /^[a-z0-9-]{1,64}$/;

/**
 * The page key of a drawer key: the cards only take lowercase slugs, so
 * «stats-productsSeries» becomes «stats-products-series».
 */
export function assistantPageKey(drawerKey: string): string {
  return drawerKey.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
}

function target(
  link: Pick<INavLink, 'key' | 'titleKey' | 'to' | 'minTier'>,
  groupTitleKey: string | null,
  tier: number
): IAssistantNavTarget {
  const result: IAssistantNavTarget = {
    key: assistantPageKey(link.key),
    titleKey: link.titleKey,
    path: link.to,
    minTier: link.minTier ?? null,
    locked: !!link.minTier && tier < link.minTier,
  };
  if (groupTitleKey) result.groupTitleKey = groupTitleKey;
  return result;
}

/**
 * Flattens the drawer into page targets (a group's own page, then its
 * sub-pages), and adds the plan settings next to the store settings, the
 * member's own account and, for CatalogManage, the menu uploader. Keys the
 * cards cannot carry are left out.
 */
export function buildAssistantNavTargets(
  input: IAssistantNavTargetsInput
): IAssistantNavTarget[] {
  const { groups, tier, basePath } = input;
  const targets: IAssistantNavTarget[] = [];

  for (const group of groups) {
    if (group.to)
      targets.push(
        target(
          {
            key: group.key,
            titleKey: group.titleKey,
            to: group.to,
            minTier: group.minTier,
          },
          null,
          tier
        )
      );
    for (const child of group.children ?? [])
      targets.push(
        target(
          { ...child, minTier: child.minTier ?? group.minTier },
          group.titleKey,
          tier
        )
      );
  }

  const settings = targets.find((entry) => entry.key === STORE_SETTINGS_TARGET);
  if (settings)
    targets.push({
      key: PLANS_TARGET,
      titleKey: 'storeSettings.plan',
      // «Configuración › Plan»: the plan tab of the store settings.
      groupTitleKey: settings.titleKey,
      path: settings.path,
      query: { tab: 'plan' },
      minTier: null,
      locked: false,
    });

  // Staff links only follow it in the user drawer: they never matter here.
  const account = buildUserNav({ isStaff: false }).find(
    (link) => link.key === ACCOUNT_TARGET
  );
  if (account) targets.push(target(account, null, tier));

  if (input.canManageCatalog)
    targets.push(
      target(
        {
          key: MENU_IMPORT_TARGET,
          titleKey: 'assistant.nav.photoTitle',
          to: `${basePath}/menu/import`,
        },
        null,
        tier
      )
    );

  return targets.filter((entry) => PAGE_KEY_PATTERN.test(entry.key));
}
